/* eslint-disable react/prop-types */
import React from 'react';
import { useSelector } from 'react-redux';
import { Link, generatePath, useParams } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbItemProps,
  BreadcrumbLink,
  BreadcrumbProps,
} from '@chakra-ui/react';

import { selectCurrentCampaign } from 'store/campaigns';
import { selectCurrentClient } from 'store/clients';
import { selectCurrentProposal } from 'store/proposals';

import {
  CAMPAIGN_LIST,
  CAMPAIGN_SHOW,
  CLIENT_LIST,
  CLIENT_SHOW,
  MARKET_LIST,
  PROPOSAL_LIST,
  PROPOSAL_SHOW,
  PRODUCT_LIST,
  AD_FORMAT_LIST,
  AD_SLOT_LIST,
  AD_FORMAT_SHOW,
  AD_SLOT_SHOW,
  USER_LIST,
  TRADING_LIST,
  //USER_SHOW,
} from 'routes';
import { selectCurrentAdFormat } from 'store/adformats';
import { selectCurrentAdSlot } from 'store/adslots';
import { useGetProductById } from 'queries/products';
import { useTranslation } from 'react-i18next';
//import { selectCurrentUser } from 'store/users';

interface BreadcrumbsProps extends BreadcrumbProps {
  children: React.ReactNode;
}

interface CurrentPageProps extends BreadcrumbItemProps {
  children: React.ReactNode;
}

const Campaigns: React.FC<BreadcrumbItemProps> = ({ ...props }) => {
  const { t } = useTranslation();
  return (
    <BreadcrumbItem {...props}>
      <BreadcrumbLink as={Link} to={generatePath(CAMPAIGN_LIST)}>
        {t('campaigns', 'Kampanie')}
      </BreadcrumbLink>
    </BreadcrumbItem>
  );
};

const Campaign: React.FC<BreadcrumbItemProps> = ({ ...props }) => {
  const { t } = useTranslation();
  const campaign = useSelector(selectCurrentCampaign);
  const campaignId = campaign?.id;
  return (
    <BreadcrumbItem {...props}>
      {campaignId ? (
        <BreadcrumbLink
          as={Link}
          to={generatePath(CAMPAIGN_SHOW, { campaignId })}
        >
          {t('campaign.nameWithLabel', { val: campaign?.name })}
        </BreadcrumbLink>
      ) : (
        '...'
      )}
    </BreadcrumbItem>
  );
};

const Clients: React.FC<BreadcrumbItemProps> = ({ ...props }) => {
  const { t } = useTranslation();
  return (
    <BreadcrumbItem {...props}>
      <BreadcrumbLink as={Link} to={generatePath(CLIENT_LIST)}>
        {t('clients', 'Klienci')}
      </BreadcrumbLink>
    </BreadcrumbItem>
  );
};

const ClientManagers: React.FC<BreadcrumbItemProps> = ({ ...props }) => {
  const { t } = useTranslation();
  return (
    <BreadcrumbItem {...props}>
      <BreadcrumbLink as={Link} to={generatePath(CLIENT_LIST)}>
        {t('clientManagers', 'Client Managers')}
      </BreadcrumbLink>
    </BreadcrumbItem>
  );
};

const Client: React.FC<BreadcrumbItemProps> = ({ ...props }) => {
  const { t } = useTranslation();
  const client = useSelector(selectCurrentClient);
  const clientId = client?.id;
  return (
    <BreadcrumbItem {...props}>
      {clientId ? (
        <BreadcrumbLink as={Link} to={generatePath(CLIENT_SHOW, { clientId })}>
          {client?.name || t('client_one', 'Klient')}
        </BreadcrumbLink>
      ) : (
        '...'
      )}
    </BreadcrumbItem>
  );
};

const Proposals: React.FC<BreadcrumbItemProps> = ({ ...props }) => {
  const { t } = useTranslation();
  return (
    <BreadcrumbItem {...props}>
      <BreadcrumbLink as={Link} to={generatePath(PROPOSAL_LIST)}>
        {t('proposals', 'Transakcje')}
      </BreadcrumbLink>
    </BreadcrumbItem>
  );
};

const Proposal: React.FC<BreadcrumbItemProps> = ({ ...props }) => {
  const { t } = useTranslation();
  const proposal = useSelector(selectCurrentProposal);
  const proposalId = proposal?.id;
  const proposalCustomId = proposal?.customId;

  const proposalIdentifier =
    String(proposalCustomId) === String(proposalId)
      ? '#' + proposalId
      : proposalCustomId;

  return (
    <BreadcrumbItem {...props}>
      {proposalId ? (
        <BreadcrumbLink
          as={Link}
          to={generatePath(PROPOSAL_SHOW, { proposalId })}
        >
          {t('proposal_one', 'Transakcja')} {proposalIdentifier}
        </BreadcrumbLink>
      ) : (
        '...'
      )}
    </BreadcrumbItem>
  );
};

const Products: React.FC<BreadcrumbItemProps> = ({ ...props }) => {
  const { t } = useTranslation();
  return (
    <BreadcrumbItem {...props}>
      <BreadcrumbLink as={Link} to={generatePath(PRODUCT_LIST)}>
        {t('products', 'Produkty')}
      </BreadcrumbLink>
    </BreadcrumbItem>
  );
};

const Product: React.FC<BreadcrumbItemProps> = ({ ...props }) => {
  const { t } = useTranslation();
  const { productId } = useParams<{ productId?: string }>();
  const { data: product } = useGetProductById(productId ?? '');

  return (
    <BreadcrumbItem {...props}>
      {productId
        ? product?.generalValue.name || t('product_one', 'Produkt')
        : '...'}
    </BreadcrumbItem>
  );
};

const Trading: React.FC<BreadcrumbItemProps> = ({ ...props }) => {
  const { t } = useTranslation();
  return (
    <BreadcrumbItem {...props}>
      <BreadcrumbLink as={Link} to={generatePath(TRADING_LIST)}>
        {t('trading_one', 'Trading')}
      </BreadcrumbLink>
    </BreadcrumbItem>
  );
};

const AdFormats: React.FC<BreadcrumbItemProps> = ({ ...props }) => {
  const { t } = useTranslation();
  return (
    <BreadcrumbItem {...props}>
      <BreadcrumbLink as={Link} to={generatePath(AD_FORMAT_LIST)}>
        {t('adFormat_other', 'Formaty reklam')}
      </BreadcrumbLink>
    </BreadcrumbItem>
  );
};

const AdFormat: React.FC<BreadcrumbItemProps> = ({ ...props }) => {
  const { t } = useTranslation();
  const adFormat = useSelector(selectCurrentAdFormat);
  const formatId = adFormat?.id;
  return (
    <BreadcrumbItem {...props}>
      {formatId ? (
        <BreadcrumbLink
          as={Link}
          to={generatePath(AD_FORMAT_SHOW, { formatId })}
        >
          {adFormat?.name || t('adFormat_one', 'Format reklamy')}
        </BreadcrumbLink>
      ) : (
        '...'
      )}
    </BreadcrumbItem>
  );
};

const AdSlots: React.FC<BreadcrumbItemProps> = ({ ...props }) => {
  const { t } = useTranslation();
  return (
    <BreadcrumbItem {...props}>
      <BreadcrumbLink as={Link} to={generatePath(AD_SLOT_LIST)}>
        {t('adSlot_other', 'Sloty reklamowe')}
      </BreadcrumbLink>
    </BreadcrumbItem>
  );
};

const AdSlot: React.FC<BreadcrumbItemProps> = ({ ...props }) => {
  const { t } = useTranslation();
  const adSlot = useSelector(selectCurrentAdSlot);
  const slotId = adSlot?.id;
  return (
    <BreadcrumbItem {...props}>
      {slotId ? (
        <BreadcrumbLink as={Link} to={generatePath(AD_SLOT_SHOW, { slotId })}>
          {adSlot?.name || t('adSlot_one', 'Slot reklamowy')}
        </BreadcrumbLink>
      ) : (
        '...'
      )}
    </BreadcrumbItem>
  );
};

const Market: React.FC<BreadcrumbItemProps> = ({ ...props }) => {
  const { t } = useTranslation();
  return (
    <BreadcrumbItem {...props}>
      <BreadcrumbLink as={Link} to={MARKET_LIST}>
        {t('market_one', 'Market')}
      </BreadcrumbLink>
    </BreadcrumbItem>
  );
};

const Users: React.FC<BreadcrumbItemProps> = ({ ...props }) => {
  const { t } = useTranslation();
  return (
    <BreadcrumbItem {...props}>
      <BreadcrumbLink as={Link} to={generatePath(USER_LIST)}>
        {t('user_other', 'Użytkownicy')}
      </BreadcrumbLink>
    </BreadcrumbItem>
  );
};
/** TODO Users Delete */
/* const User = ({ ...props }) => {
  const {t} = useTranslation();
  const { id: userId } = useSelector(selectCurrentUser);
  return (
    <BreadcrumbItem {...props}>
      {userId ? (
        <BreadcrumbLink as={Link} to={generatePath(USER_SHOW, { userId })}>
          &lt; {t('back', 'Powrót')}
        </BreadcrumbLink>
      ) : (
        '...'
      )}
    </BreadcrumbItem>
  );
}; */

const CurrentPage: React.FC<CurrentPageProps> = ({
  children,
  ...props
}): JSX.Element => (
  <BreadcrumbItem {...props} isCurrentPage>
    <BreadcrumbLink>{children}</BreadcrumbLink>
  </BreadcrumbItem>
);

const Breadcrumbs = ({ children, ...props }: BreadcrumbsProps): JSX.Element => (
  <Breadcrumb fontSize='sm' fontWeight='medium' separator='»' {...props}>
    {children}
  </Breadcrumb>
);

Breadcrumbs.Campaigns = Campaigns;
Breadcrumbs.Campaign = Campaign;
Breadcrumbs.Clients = Clients;
Breadcrumbs.Client = Client;
Breadcrumbs.ClientManagers = ClientManagers;
Breadcrumbs.Proposals = Proposals;
Breadcrumbs.Proposal = Proposal;
Breadcrumbs.Products = Products;
Breadcrumbs.Product = Product;
Breadcrumbs.Trading = Trading;
Breadcrumbs.AdFormats = AdFormats;
Breadcrumbs.AdFormat = AdFormat;
Breadcrumbs.AdSlots = AdSlots;
Breadcrumbs.AdSlot = AdSlot;
Breadcrumbs.Market = Market;
Breadcrumbs.Users = Users;
//Breadcrumbs.User = User;
Breadcrumbs.CurrentPage = CurrentPage;

export default Breadcrumbs;
